<template>
  <div class="min-h-dvh bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white px-4 py-3">
      <div class="max-w-xl mx-auto flex items-center justify-between gap-4">
        <a :href="backUrl" class="text-emerald-100 hover:text-white text-sm shrink-0">&larr; Course</a>
        <h1 class="text-sm font-semibold tracking-wide whitespace-nowrap">Vocabulary Review</h1>
        <!-- Hidden until a deck exists; it otherwise reads "1 / 0" while loading
             and on both empty states. -->
        <span v-if="words.length && !sessionComplete" class="text-sm font-semibold tabular-nums shrink-0">
          {{ currentIndex + 1 }} / {{ words.length }}
        </span>
        <span v-else class="w-12 shrink-0" aria-hidden="true"></span>
      </div>
    </header>

    <!-- Short states (loading / error / empty / complete) are vertically centred
         rather than pinned to the top of a 1080px viewport. 100dvh, not 100vh:
         on iOS Safari 100vh includes the area behind the address bar. -->
    <main v-if="loading || error || !words.length || sessionComplete"
          class="max-w-3xl mx-auto px-4 flex flex-col items-center justify-center text-center
                 min-h-[calc(100dvh-2.75rem)]">
      <div v-if="loading">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-600"></div>
        <p class="mt-4 text-gray-500 dark:text-gray-400">Loading review words&hellip;</p>
      </div>

      <!-- A failed fetch used to fall through to "All caught up!", telling the
           learner they had finished when nothing had loaded. -->
      <div v-else-if="error">
        <p class="text-4xl mb-4">&#9888;</p>
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50">Couldn't load your review</h2>
        <p class="text-gray-500 dark:text-gray-400 mt-2">Check your connection and try again.</p>
        <button @click="loadWords"
                class="mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Try again
        </button>
      </div>

      <div v-else-if="!words.length && mode === 'quick'">
        <p class="text-4xl mb-4">&#128218;</p>
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50">Nothing to review yet</h2>
        <p class="text-gray-500 dark:text-gray-400 mt-2">Complete a lesson first to unlock vocabulary review.</p>
        <a :href="backUrl" class="inline-block mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Go to lessons
        </a>
      </div>

      <div v-else-if="!words.length && mode === 'deck'">
        <p class="text-4xl mb-4">&#128278;</p>
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50">Nothing saved here yet</h2>
        <p class="text-gray-500 dark:text-gray-400 mt-2">Tap any word while reading to add it to your deck.</p>
        <a href="/review/deck" class="inline-block mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Open My Deck
        </a>
      </div>

      <div v-else-if="!words.length">
        <p class="text-4xl mb-4">&#10003;</p>
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50">All caught up!</h2>
        <p class="text-gray-500 dark:text-gray-400 mt-2">No vocabulary words are due for review right now.</p>
        <a :href="backUrl" class="inline-block mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Back to Course
        </a>
      </div>

      <div v-else>
        <p class="text-6xl mb-4">&#127793;</p>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50">Review complete</h2>
        <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Reviewed <span class="font-bold text-emerald-600 dark:text-emerald-400">{{ words.length }}</span> words
        </p>
        <p v-if="totalXpEarned > 0" class="text-sm text-gray-500 dark:text-gray-400 mt-2">
          +{{ totalXpEarned }} XP earned
        </p>
        <a :href="backUrl" class="inline-block mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Back to Course
        </a>
      </div>
    </main>

    <!-- Active session. Full-bleed on mobile (one focused task), but a bounded
         panel from lg up: a 576px column floating in a 1920px void has no figure
         against the ground, and centring it vertically strands the rating buttons
         half a screen from the answer they judge. The panel caps that distance. -->
    <div v-else
         class="min-h-[calc(100dvh-2.75rem)] flex flex-col lg:items-center lg:justify-center lg:py-6">
      <div class="w-full flex flex-col flex-1
                  lg:flex-none lg:max-w-xl lg:min-h-[30rem] lg:rounded-3xl lg:border
                  lg:border-gray-200 dark:lg:border-gray-700 lg:bg-white dark:lg:bg-gray-800
                  lg:shadow-sm lg:overflow-hidden">

        <div class="h-1 w-full bg-gray-200 dark:bg-gray-700 shrink-0">
          <div class="h-1 bg-emerald-500 transition-all duration-300"
               :style="{ width: `${(currentIndex / words.length) * 100}%` }"></div>
        </div>

        <div class="flex justify-end px-4 pt-3 shrink-0">
          <AutoplayToggle />
        </div>

        <!-- The flip target. A bare div with @click gave keyboard and screen-reader
             users no way in, and for an SRS tool that is also a usability failure:
             Anki users expect Space to flip and 1-4 to rate. Reveal is one-way — a
             stray second tap used to un-reveal and collapse the rating row. -->
        <!-- A plain container, NOT role="button": it holds four focusable
             controls, and a button's children are presentational to assistive
             tech. Whole-card click stays as a convenience; the explicit button
             below is the real, focusable affordance. -->
        <div
          class="flex-1 flex flex-col justify-center text-center px-5 py-4"
          :class="showAnswer ? '' : 'cursor-pointer'"
          @click="onCardClick"
        >
          <!-- Front -->
          <div v-if="!showAnswer">
            <p class="text-xs uppercase tracking-widest text-gray-400">What does this word mean?</p>

            <h2 class="mt-6 text-5xl sm:text-6xl font-bold text-gray-900 dark:text-gray-50 leading-snug">
              {{ currentWord.word_vietnamese }}
            </h2>

            <!-- Pronunciation reads as one block. Romanization is hidden when it
                 merely repeats the word; IPA is unreliable for single letters. -->
            <div class="mt-3 space-y-1">
              <p v-if="romanizationDiffers(currentWord)" class="text-base text-gray-500 dark:text-gray-400">
                <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1">{{ currentWord.part_of_speech === 'letter' ? 'called' : 'pronounced' }}</span>{{ currentWord.word_romanization }}
              </p>
              <PhoneticGuide v-if="currentWord.part_of_speech !== 'letter'"
                             :ipa="currentWord.phonetic_ipa"
                             :simplified="currentWord.phonetic_simplified"
                             class="justify-center" />
            </div>

            <AudioButton ref="wordAudio" :src="currentWord.audio_url" :text="currentWord.word_vietnamese"
                         :show-slow="true" class="mx-auto mt-7" @click.stop />

            <!-- Strength sits apart from the reveal hint: adjacent, four small
                 dots beside an interaction cue read as deck pagination. -->
            <div class="mt-8 flex justify-center">
              <StrengthDots :strength="strengthOf(currentWord)" />
            </div>
            <button
              ref="revealBtn"
              type="button"
              class="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 transition-colors"
              @click.stop="reveal"
            >
              Show answer
              <kbd class="text-[10px] font-sans font-normal opacity-80 border border-white/40 rounded px-1">Space</kbd>
            </button>
          </div>

          <!-- Back -->
          <div v-else>
            <p class="text-xs uppercase tracking-widest text-gray-400">Answer</p>

            <h2 class="mt-4 text-4xl sm:text-5xl font-bold text-emerald-700 dark:text-emerald-400 leading-snug">
              {{ currentWord.word_english }}
            </h2>

            <p class="mt-3 text-lg text-gray-700 dark:text-gray-200">
              {{ currentWord.word_vietnamese }}<template v-if="romanizationDiffers(currentWord)"> <span class="text-gray-400">({{ currentWord.word_romanization }})</span></template>
            </p>
            <p class="mt-1 text-xs uppercase tracking-wide text-gray-400">{{ currentWord.part_of_speech }}</p>

            <!-- Tinted, not a flat white/gray: the page is gray-50 on mobile and
                 the panel is white at lg, so any single neutral fill disappears
                 against one of them. Emerald also reads as "example". -->
            <div v-if="currentWord.example_sentence_vn"
                 class="mt-6 text-left bg-emerald-50/70 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40 p-4 rounded-2xl">
              <div class="flex items-start justify-between gap-3">
                <p class="font-medium text-gray-800 dark:text-gray-100">{{ currentWord.example_sentence_vn }}</p>
                <!-- The payload has carried example_audio_url all along and the
                     card never used it. This is the only connected speech on the
                     screen, in a tonal language. -->
                <AudioButton v-if="currentWord.example_audio_url"
                             :src="currentWord.example_audio_url"
                             :text="currentWord.example_sentence_vn"
                             class="shrink-0" @click.stop />
              </div>
              <p class="text-gray-500 dark:text-gray-400 text-sm mt-1">{{ currentWord.example_sentence_en }}</p>
            </div>

            <!-- Deliberately lighter than a rating button: it leaves the session
                 with no resume, so it must not compete with the primary action. -->
            <a
              v-if="wordDetailHref"
              :href="wordDetailHref"
              target="_blank"
              rel="noopener"
              @click.stop
              class="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-emerald-700 dark:hover:text-emerald-400 underline underline-offset-4 decoration-gray-300 hover:decoration-emerald-400"
            >
              Tone curve &amp; word details
              <span aria-hidden="true">&#8599;</span>
              <span class="sr-only">(opens in a new tab)</span>
            </a>
          </div>
        </div>

        <!-- Space is reserved on the front too, so revealing swaps content rather
             than growing the panel and shifting the word under the reader's eye. -->
        <div class="px-4 pb-5 pt-1 shrink-0"
             :class="showAnswer ? '' : 'invisible pointer-events-none'"
             :aria-hidden="!showAnswer">
          <!-- Rating writes were fire-and-forget: a dropped connection or the 403
               from require_subscription_for discarded the grade while the summary
               still claimed the word was reviewed. -->
          <p v-if="saveError" class="mb-2 text-center text-xs font-medium text-red-600 dark:text-red-400">
            Couldn't save that rating &mdash; check your connection and try again.
          </p>
          <div class="grid grid-cols-4 gap-2">
            <button v-for="r in ratings" :key="r.grade"
                    :disabled="saving"
                    @click="rateWord(r.grade)"
                    :class="['py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed', r.cls]">
              {{ r.label }}
              <kbd class="ml-1 text-[10px] font-sans font-normal opacity-70">{{ r.key }}</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { apiGet, apiPost } from '../api/client'
import AudioButton from './shared/AudioButton.vue'
import AutoplayToggle from './shared/AutoplayToggle.vue'
import PhoneticGuide from './shared/PhoneticGuide.vue'
import StrengthDots from './shared/StrengthDots.vue'
import { prefetchStaticAudio } from '../utils/ttsCache'
import { autoplayEnabled } from '../utils/autoplayPref'
import { romanizationDiffers } from '../utils/vocab'

const props = defineProps({
  backUrl: { type: String, required: true },
})

const words = ref([])
const currentIndex = ref(0)
const loading = ref(true)
const error = ref(false)
const saving = ref(false)
const saveError = ref(false)
const revealBtn = ref(null)

// Emerald sits on GOOD, the default action in spaced repetition. Putting the
// brand colour on "Easy" visually recommends it, which inflates intervals and
// quietly degrades retention. Amber/orange stays reserved for due-review nudges,
// so "Hard" is neutral rather than orange.
const ratings = [
  { grade: 0, key: '1', label: 'Again', cls: 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 dark:hover:bg-red-900/60' },
  { grade: 2, key: '2', label: 'Hard',  cls: 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600' },
  { grade: 4, key: '3', label: 'Good',  cls: 'bg-emerald-600 text-white hover:bg-emerald-700' },
  { grade: 5, key: '4', label: 'Easy',  cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800' },
]
const showAnswer = ref(false)
const sessionComplete = ref(false)
const totalXpEarned = ref(0)

const currentWord = computed(() => words.value[currentIndex.value] || {})
const wordAudio = ref(null)

// Guarded: without a slug or id this produced the string "/words/undefined".
const wordDetailHref = computed(() => {
  const w = currentWord.value
  const key = w.slug || w.vocabulary_word_id
  return key ? `/words/${key}` : null
})

// Reveal is ONE-WAY. As a toggle, a second stray tap hid the answer again and
// collapsed the rating row out from under the user's finger.
function reveal() {
  showAnswer.value = true
}

// Clicking the card reveals, but not when the click came from a control the card
// contains (audio, IPA toggle, details link, the reveal button itself).
function onCardClick(e) {
  if (showAnswer.value) return
  if (e.target.closest('button, a, input, [role="button"]')) return
  reveal()
}

// Mirrors VocabReview#strength on the server (0 = not enrolled).
function strengthOf(w) {
  if (!w || w.review_id == null) return 0
  if (!w.repetitions) return 1
  if (w.interval_days >= 21) return 4
  if (w.interval_days >= 6) return 3
  return 2
}

const query = new URLSearchParams(window.location.search)
const mode = query.get('mode')

// My Deck links here carrying source_kind/source_id/shuffle/limit ("Study these
// 12 →"). They used to be dropped on the floor and every link served the same
// due queue, so a story-scoped study session studied whatever happened to be
// due. Forward them verbatim; the API validates them.
function dueUrl() {
  if (mode === 'quick') return '/api/v1/vocab_reviews/due?mode=quick'
  if (mode !== 'deck') return '/api/v1/vocab_reviews/due'

  const forwarded = new URLSearchParams({ mode: 'deck' })
  for (const key of ['source_kind', 'source_id', 'shuffle', 'limit']) {
    if (query.get(key)) forwarded.set(key, query.get(key))
  }
  return `/api/v1/vocab_reviews/due?${forwarded.toString()}`
}

// Auto-play word audio when card changes and prefetch next
watch(currentIndex, async () => {
  if (!showAnswer.value && words.value.length) {
    await nextTick()
    if (autoplayEnabled.value) wordAudio.value?.play()
    const nextWord = words.value[currentIndex.value + 1]
    if (nextWord?.audio_url) prefetchStaticAudio([nextWord.audio_url])
  }
})

function onKey(e) {
  if (e.repeat) return
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
  if (loading.value || error.value || sessionComplete.value || !words.value.length) return
  if (saving.value) return
  if (!showAnswer.value) {
    // Only the reveal path defers to a focused control: Space on the audio
    // button must replay audio, not flip the card. The rating digits must keep
    // working regardless of what has focus — gating them here made 1-4 dead
    // after tapping the example-audio button, which is the natural thing to do
    // on the back of the card.
    if (e.target instanceof HTMLElement && e.target.closest('button, a')) return
    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); reveal() }
    return
  }
  const grade = { 1: 0, 2: 2, 3: 4, 4: 5 }[e.key]
  if (grade !== undefined) { e.preventDefault(); rateWord(grade) }
}

onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

// Named so the error state's "Try again" can call it. Inline in onMounted it was
// unreachable, and a failed fetch left the learner on a dead end.
async function loadWords() {
  loading.value = true
  error.value = false
  currentIndex.value = 0
  showAnswer.value = false
  try {
    words.value = await apiGet(dueUrl())
  } catch (e) {
    console.error('Failed to load review words:', e)
    error.value = true
  } finally {
    loading.value = false
  }
  // Prefetch all audio URLs and auto-play first card
  if (words.value.length) {
    const audioUrls = words.value.map(w => w.audio_url).filter(Boolean)
    if (audioUrls.length) prefetchStaticAudio(audioUrls)
    await nextTick()
    if (autoplayEnabled.value) wordAudio.value?.play()
  }
}

onMounted(loadWords)

async function rateWord(quality) {
  // Without an in-flight guard, holding a number key fired one POST per repeat
  // for the SAME word — each compounding interval_days and ease_factor on the
  // server — and then advanced that many cards, skipping unreviewed words.
  if (saving.value) return
  saving.value = true
  saveError.value = false
  try {
    const result = await apiPost('/api/v1/vocab_reviews/record', {
      vocabulary_word_id: currentWord.value.vocabulary_word_id,
      quality: quality,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    totalXpEarned.value += result.xp_earned || 0
  } catch (e) {
    // Do NOT advance. Silently dropping the grade and moving on told the learner
    // they had reviewed a word whose schedule never changed.
    console.warn('Failed to record review:', e)
    saveError.value = true
    saving.value = false
    return
  }

  if (currentIndex.value < words.value.length - 1) {
    currentIndex.value++
    showAnswer.value = false
    // Rating with the mouse leaves focus on a button that is about to become
    // invisible + aria-hidden, dropping focus to <body>.
    await nextTick()
    revealBtn.value?.focus?.()
  } else {
    sessionComplete.value = true
  }
  saving.value = false
}
</script>
